import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Grid,
    Stack,
    Chip,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    CircularProgress
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { getPublishedArticles } from "../../Services/ApiCalls";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { fetchAllAlbums } from "../../store/albumsSlice";
import { Article } from '../../types';
import Seo from '../../Components/Seo';

type SearchType = 'all' | 'articles' | 'albums';

interface SearchResult {
    type: 'article' | 'blog' | 'album';
    id: number;
    title: string;
    description?: string;
    excerpt?: string;
    coverImage?: string;
    slug?: string;
    category?: string;
    publishedAt?: string;
}

const isBlog = (excerpt: string | null | undefined): boolean => {
    if (!excerpt) return false;
    return excerpt.toLowerCase().includes('blog');
};

export default function Search() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    
    const [searchTerm, setSearchTerm] = useState(query);
    const [searchType, setSearchType] = useState<SearchType>('all');
    const [articles, setArticles] = useState<Article[]>([]);
    const [articlesLoading, setArticlesLoading] = useState(false);
    
    // Get albums from Redux store
    const { albums, loading: albumsLoading } = useSelector((state: RootState) => state.albums);

    // Fetch albums from Redux if not already loaded
    useEffect(() => {
        if (albums.length === 0 && !albumsLoading) {
            dispatch(fetchAllAlbums());
        }
    }, [dispatch, albums.length, albumsLoading]);

    // Fetch articles
    useEffect(() => {
        const fetchArticles = async () => {
            try {
                setArticlesLoading(true);
                const articlesRes = await getPublishedArticles(1, 1000);
                setArticles(articlesRes.articles || []);
            } catch (e) {
                console.error('Error fetching articles:', e);
            } finally {
                setArticlesLoading(false);
            }
        };
        fetchArticles();
    }, []);

    const loading = articlesLoading || albumsLoading;

    // Update URL when search term changes
    useEffect(() => {
        if (searchTerm) {
            setSearchParams({ q: searchTerm });
        } else {
            setSearchParams({});
        }
    }, [searchTerm, setSearchParams]);

    // Search function
    const searchInText = (text: string | null | undefined, term: string): boolean => {
        if (!text || !term) return false;
        return text.toLowerCase().includes(term.toLowerCase());
    };

    // Filter results based on search term
    const searchResults = useMemo(() => {
        if (!query.trim()) return [];

        const results: SearchResult[] = [];

        // Search articles
        if (searchType === 'all' || searchType === 'articles') {
            articles.forEach(article => {
                const matches = 
                    searchInText(article.title, query) ||
                    searchInText(article.excerpt, query);
                
                if (matches) {
                    results.push({
                        type: isBlog(article.excerpt) ? 'blog' : 'article',
                        id: article.id,
                        title: article.title,
                        excerpt: article.excerpt || undefined,
                        coverImage: article.coverImage || undefined,
                        slug: article.slug,
                        publishedAt: article.publishedAt || undefined,
                    });
                }
            });
        }

        // Search albums
        if (searchType === 'all' || searchType === 'albums') {
            albums.forEach(album => {
                const matches = 
                    searchInText(album.title, query) ||
                    searchInText(album.description, query);
                
                if (matches) {
                    results.push({
                        type: 'album',
                        id: album.id,
                        title: album.title,
                        description: album.description,
                        coverImage: album.coverImage || undefined,
                        category: album.category,
                    });
                }
            });
        }

        return results;
    }, [query, articles, albums, searchType]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setSearchTerm(searchTerm.trim());
    };

    const handleSearchTypeChange = (_: React.SyntheticEvent, newValue: SearchType) => {
        setSearchType(newValue);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1200, mx: 'auto' }}>
            <Seo
                title="Search"
                description="Search Radiology Illustration for educational radiology articles, blogs, infographics, illustrations, publications, and visual learning albums."
                canonicalPath="/search"
            />
            <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                    fontWeight: 700, 
                    mb: { xs: 2, sm: 3 },
                    fontSize: {
                        xs: '1.5rem',
                        sm: '2rem',
                        md: '2.5rem',
                    }
                }}
            >
                Search
            </Typography>

            {/* Search Bar */}
            <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search articles, blogs, albums..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 2 }}
                />
            </Box>

            {/* Filter Tabs */}
            {query && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 }, overflowX: 'auto' }}>
                    <Tabs 
                        value={searchType} 
                        onChange={handleSearchTypeChange} 
                        aria-label="search type tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 80, sm: 120 },
                                px: { xs: 1, sm: 2 },
                            }
                        }}
                    >
                        <Tab label={`All (${searchResults.length})`} value="all" />
                        <Tab 
                            label={`Articles & Blogs (${searchResults.filter(r => r.type === 'article' || r.type === 'blog').length})`} 
                            value="articles" 
                        />
                        <Tab 
                            label={`Albums (${searchResults.filter(r => r.type === 'album').length})`} 
                            value="albums" 
                        />
                    </Tabs>
                </Box>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Search Results */}
            {!loading && query && searchResults.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                        No results found for "{query}"
                    </Typography>
                </Box>
            )}

            {!loading && !query && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                        Enter a search term to find articles, blogs, and albums
                    </Typography>
                </Box>
            )}

            {!loading && query && searchResults.length > 0 && (
                <Grid container spacing={3}>
                    {searchResults.map((result) => (
                        <Grid key={`${result.type}-${result.id}`} size={{ xs: 12, md: 6, lg: 4 }}>
                            <Card 
                                sx={{ 
                                    height: "100%", 
                                    display: "flex", 
                                    flexDirection: "column",
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 3
                                    }
                                }}
                                onClick={() => {
                                    if (result.type === 'article' || result.type === 'blog') {
                                        navigate(`/articles/${result.slug}`);
                                    } else if (result.type === 'album') {
                                        navigate(`/album/${result.id}`);
                                    }
                                }}
                            >
                                {result.coverImage && (
                                    <CardMedia 
                                        component="img" 
                                        image={result.coverImage} 
                                        alt={result.title} 
                                        sx={{ height: 200, objectFit: "cover" }} 
                                    />
                                )}
                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2 } }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                                        <Chip 
                                            label={
                                                result.type === 'blog' ? 'Blog' :
                                                result.type === 'article' ? 'Article' :
                                                result.category || 'Album'
                                            }
                                            color={
                                                result.type === 'blog' ? 'primary' :
                                                result.type === 'article' ? 'success' :
                                                'default'
                                            }
                                            size="small" 
                                            variant="outlined"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Stack>
                                    <Typography 
                                        variant="h6" 
                                        sx={{ 
                                            fontWeight: 600, 
                                            mb: 1,
                                            fontSize: {
                                                xs: '1rem',
                                                sm: '1.25rem',
                                            },
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {result.title}
                                    </Typography>
                                    {(result.excerpt || result.description) && (
                                        <Typography 
                                            variant="body2" 
                                            color="text.secondary" 
                                            sx={{ 
                                                mb: 2, 
                                                flex: 1,
                                                fontSize: {
                                                    xs: '0.8rem',
                                                    sm: '0.875rem',
                                                },
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {result.excerpt || result.description}
                                        </Typography>
                                    )}
                                    {result.publishedAt && (
                                        <Typography 
                                            variant="caption" 
                                            color="text.secondary"
                                            sx={{ 
                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                display: { xs: 'none', sm: 'block' }
                                            }}
                                        >
                                            {formatDate(result.publishedAt)}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
}
